import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAdminApi } from '../../hooks/useAdminApi';
import AdminLayout from '../../components/admin/AdminLayout';
import EditTenantModal from '../../components/admin/EditTenantModal';
import ProvisionTenantModal from '../../components/admin/ProvisionTenantModal';
import {
  Search,
  Filter,
  Building2,
  Eye,
  Edit,
  Key,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  Server,
  Download,
  Globe,
  AlertTriangle,
  X,
  CreditCard,
  Loader2,
  Ban
} from 'lucide-react';

const TenantsListPage = () => {
  const { get, loading } = useAdminApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenants, setTenants] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [planFilter, setPlanFilter] = useState(searchParams.get('plan') || '');
  const [domainFilter, setDomainFilter] = useState(searchParams.get('domain') || '');
  const [refreshing, setRefreshing] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRefs = useRef({});

  // Modals
  const [editModal, setEditModal] = useState({ open: false, tenant: null });
  const [provisionModal, setProvisionModal] = useState({ open: false, tenant: null });

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setDropdownOpen(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleDropdownToggle = (tenantId) => {
    if (dropdownOpen === tenantId) {
      setDropdownOpen(null);
    } else {
      const button = buttonRefs.current[tenantId];
      if (button) {
        const rect = button.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right
        });
      }
      setDropdownOpen(tenantId);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [pagination.page, statusFilter, planFilter, domainFilter]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (pagination.page === 1) {
        fetchTenants();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [search]);

  const fetchTenants = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (planFilter) params.append('plan', planFilter);
      if (domainFilter) params.append('domainType', domainFilter);

      const response = await get(`/tenants?${params.toString()}`);
      if (response.success) {
        setTenants(response.data || response.tenants || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 1
        }));
      }
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    const newParams = new URLSearchParams(searchParams);
    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status');
    }
    setSearchParams(newParams);
  };

  const handlePlanChange = (plan) => {
    setPlanFilter(plan);
    const newParams = new URLSearchParams(searchParams);
    if (plan) {
      newParams.set('plan', plan);
    } else {
      newParams.delete('plan');
    }
    setSearchParams(newParams);
  };

  const handleDomainChange = (domain) => {
    setDomainFilter(domain);
    const newParams = new URLSearchParams(searchParams);
    if (domain) {
      newParams.set('domain', domain);
    } else {
      newParams.delete('domain');
    }
    setSearchParams(newParams);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'payment_received':
        return <CreditCard className="w-4 h-4 text-blue-500" />;
      case 'provisioning':
        return <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />;
      case 'suspended':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'terminated':
        return <Ban className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      payment_received: 'bg-blue-100 text-blue-700',
      provisioning: 'bg-purple-100 text-purple-700',
      suspended: 'bg-red-100 text-red-700',
      terminated: 'bg-gray-100 text-gray-700',
      inactive: 'bg-gray-100 text-gray-700'
    };

    const labels = {
      active: 'Active',
      pending: 'Pending',
      payment_received: 'Paid',
      provisioning: 'Provisioning',
      suspended: 'Suspended',
      terminated: 'Terminated'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {getStatusIcon(status)}
        <span className="ml-1.5">{labels[status] || status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'}</span>
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    if (!plan) {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
          No Plan
        </span>
      );
    }
    const styles = {
      starter: 'bg-blue-100 text-blue-700',
      professional: 'bg-purple-100 text-purple-700',
      enterprise: 'bg-pink-100 text-pink-700',
      trial: 'bg-orange-100 text-orange-700'
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[plan] || styles.starter}`}>
        {plan.charAt(0).toUpperCase() + plan.slice(1)}
      </span>
    );
  };

  const hasActiveFilters = statusFilter || planFilter || domainFilter || search;

  const clearAllFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPlanFilter('');
    setDomainFilter('');
    setSearchParams(new URLSearchParams());
  };

  const getDomainDisplay = (tenant) => {
    const domainType = tenant.domain?.type;
    const subdomain = tenant.domain?.subdomain || tenant.subdomain;
    const customDomain = tenant.domain?.customDomain;
    const isConfigured = tenant.domain?.isConfigured;

    if (domainType === 'custom' && customDomain) {
      return (
        <div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-purple-500 flex-shrink-0" />
            <span className="text-gray-900 font-mono text-sm">{customDomain}</span>
            {isConfigured ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Live
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Pending
              </span>
            )}
          </div>
        </div>
      );
    }

    if (subdomain) {
      return (
        <span className="text-gray-900 font-mono text-sm">
          {subdomain}.bestohrms.com
        </span>
      );
    }

    return (
      <span className="text-gray-400 text-sm italic">Not configured</span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleEditClick = (tenant) => {
    setEditModal({ open: true, tenant });
    setDropdownOpen(null);
  };

  const handleProvisionClick = (tenant) => {
    setProvisionModal({ open: true, tenant });
    setDropdownOpen(null);
  };

  const handleModalClose = (refreshData = false) => {
    setEditModal({ open: false, tenant: null });
    setProvisionModal({ open: false, tenant: null });
    if (refreshData) {
      fetchTenants();
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
              {pagination.total > 0 && (
                <span className="px-2.5 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                  {pagination.total}
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1">Manage all registered tenants</p>
          </div>
          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 mr-1" />
                Clear filters
              </button>
            )}
            <button
              onClick={fetchTenants}
              disabled={refreshing}
              className="flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by company name, email, or subdomain..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="payment_received">Paid</option>
                <option value="provisioning">Provisioning</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="terminated">Terminated</option>
              </select>
              <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Plan Filter */}
            <div className="relative">
              <select
                value={planFilter}
                onChange={(e) => handlePlanChange(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Plans</option>
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Domain Filter */}
            <div className="relative">
              <select
                value={domainFilter}
                onChange={(e) => handleDomainChange(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent cursor-pointer"
              >
                <option value="">All Domains</option>
                <option value="subdomain">Subdomain Only</option>
                <option value="custom">Custom Domain</option>
                <option value="custom_pending">Custom (Pending Setup)</option>
              </select>
              <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Tenants Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden relative">
          {/* Loading Overlay */}
          {refreshing && tenants.length > 0 && (
            <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
              <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg shadow-md">
                <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
                <span className="text-gray-600 font-medium">Refreshing...</span>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {refreshing && tenants.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <RefreshCw className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Loading tenants...</p>
                    </td>
                  </tr>
                ) : tenants.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 font-medium">No tenants found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {hasActiveFilters ? 'Try adjusting your search or filters' : 'No tenants have registered yet'}
                      </p>
                      {hasActiveFilters && (
                        <button
                          onClick={clearAllFilters}
                          className="mt-3 text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          Clear all filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  tenants.map((tenant) => (
                    <tr key={tenant._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-5 h-5 text-white" />
                          </div>
                          <div className="ml-3">
                            <p className="font-medium text-gray-900">{tenant.companyName}</p>
                            <p className="text-sm text-gray-500">{tenant.subscription?.maxEmployees || 0} employees max</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-900">{tenant.superadmin?.name || tenant.contactPerson}</p>
                        <p className="text-sm text-gray-500">{tenant.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        {getPlanBadge(tenant.subscription?.plan || tenant.plan)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(tenant.provisioningStatus || tenant.subscription?.status || tenant.status)}
                      </td>
                      <td className="px-6 py-4">
                        {getDomainDisplay(tenant)}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDate(tenant.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          ref={(el) => buttonRefs.current[tenant._id] = el}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDropdownToggle(tenant._id);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-500" />
                        </button>

                        {dropdownOpen === tenant._id && (
                          <>
                            {/* Backdrop overlay */}
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setDropdownOpen(null)}
                            />
                            <div
                              className="fixed w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50"
                              style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
                            >
                              <Link
                                to={`/admin/tenants/${tenant._id}`}
                                className="flex items-center px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                                onClick={() => setDropdownOpen(null)}
                              >
                                <Eye className="w-4 h-4 mr-3 text-gray-400" />
                                View Details
                              </Link>
                              <button
                                onClick={() => handleEditClick(tenant)}
                                className="w-full flex items-center px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Edit className="w-4 h-4 mr-3 text-gray-400" />
                                Edit Tenant
                              </button>
                              <button
                                onClick={() => handleProvisionClick(tenant)}
                                className="w-full flex items-center px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Server className="w-4 h-4 mr-3 text-gray-400" />
                                Provision
                              </button>
                              <div className="border-t border-gray-100 my-1" />
                              <Link
                                to={`/admin/licenses?tenant=${tenant._id}`}
                                className="flex items-center px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                                onClick={() => setDropdownOpen(null)}
                              >
                                <Key className="w-4 h-4 mr-3 text-gray-400" />
                                View Licenses
                              </Link>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} tenants
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="px-4 py-2 text-sm font-medium text-gray-700">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {editModal.open && (
        <EditTenantModal
          tenant={editModal.tenant}
          onClose={() => handleModalClose(false)}
          onSave={() => handleModalClose(true)}
        />
      )}

      {provisionModal.open && (
        <ProvisionTenantModal
          tenant={provisionModal.tenant}
          onClose={() => handleModalClose(false)}
          onSave={() => handleModalClose(true)}
        />
      )}
    </AdminLayout>
  );
};

export default TenantsListPage;
